<script>
  import { onMount, getContext } from "svelte";
  import { navigate } from "svelte-routing";
  import uniqBy from "lodash/uniqBy";

  import SendIcon from "@local/assets/icons/share.svg";
  import Datatable from "#lib/datatable/Datatable.svelte";
  import { Modal } from "svelte-utilities";
  import TextInput from "#lib/sidebar/TextInput.svelte";
  import ErrorMessage from "#lib/standard-inputs/ErrorMessage.svelte";

  import isEmail from "@local/extensions/validators/is-email.js";
  import { api, addCustomer, resendCustomerInvitation } from "#src/api";
  import { profile } from "#src/stores/auth.js";
  import { objectify } from "overline";

  const org = getContext("org");
  const productLists = getContext("productLists");

  let customers = [];
  let removeCustomerModal;
  let newCustomerModal;
  let resendInvitationModal;
  let invitationToResend = null;
  let error;
  let customerToRemove = null;
  let name = "";
  let address = "";
  let user_email = "";

  $: canAdmin = $profile && ["org_admin", "developer"].includes($profile.user_role);
  $: plists = objectify($productLists || []);
  $: customerProps = [
    { label: "Org Name", prop: "name", type: "text" },
    { label: "Email", prop: "primary_contact_email", type: "text" },
    { label: "Address", prop: "address", type: "text" },
    {
      label: "Product List",
      prop: "business_relation.product_list_id",
      formatter: (v, c) => productList(v, c),
      editable: false,
    },
    { label: "Confirmed", prop: "confirmed_at", type: "boolean" },
  ];

  function productList(id, customer) {
    const products = customer?.business_relation?.products;
    if (products === "all") {
      return "All";
    } else if (products === "none") {
      return "None";
    } else if (id) {
      return plists[id]?.name || "";
    } else {
      return "None";
    }
  }

  function beginAddingCustomer() {
    name = "";
    address = "";
    user_email = "";
    newCustomerModal.open();
  }

  function confirmRemoveCustomer(e) {
    customerToRemove = customers[e.detail.index];
    removeCustomerModal.open();
  }

  async function removeCustomer() {
    const c = await api
      .from("business_relations")
      .delete()
      .eq("seller_id", $org.id)
      .eq("buyer_id", customerToRemove.id);

    if (!c.error) {
      await fetchCustomers();
    }
    removeCustomerModal.close();
  }

  async function fetchCustomers() {
    const c = await api
      .from("business_relations")
      .select("*,buyer: buyer_id(*)")
      .eq("seller_id", $org.id)
      .not("buyer_id", "is", null);

    if (!c.error) {
      let cs;
      if ($profile?.user_role === "developer") {
        cs = c.data.map((c) => ({ ...c.buyer, business_relation: c }));
      } else {
        cs = c.data
          .map((c) => ({ ...c.buyer, business_relation: c }))
          .filter((c) => c.confirmed_at || c.invited_by === $org.id);
      }
      customers = uniqBy(cs, "id");
    }
  }

  async function inviteCustomer() {
    const { error: cError } = await addCustomer({
      name,
      address,
      user_email,
      seller_id: $org.id,
    });

    if (cError) {
      error = cError.message;
    }

    await fetchCustomers();
    if (newCustomerModal) newCustomerModal.close();
  }

  function confirmResendInvite(id) {
    invitationToResend = id;
    resendInvitationModal.open();
  }

  async function resendInvite() {
    if (!invitationToResend) return;

    const { error: cError } = await resendCustomerInvitation({
      buyer_id: invitationToResend,
      seller_id: $org.id,
    });

    if (cError) {
      error = cError.message;
    }
  }

  function clickCustomer(e) {
    const { id } = e.detail;
    navigate(`/settings/customers/${id}`);
  }

  onMount(() => {
    fetchCustomers();
  });
</script>

<div class="px-4 pb-4 border-b border-gray-300">
  <h2>Customers</h2>
</div>

<div class="py-4 sm:px-4 flex justify-end items-center">
  <button class="btn btn-primary" on:click={beginAddingCustomer}>+ Add Customer</button>
</div>

<ErrorMessage message={error} on:close={() => (error = null)} />
<div class="bg-white border rounded-lg overflow-hidden">
  <Datatable
    data={customers}
    columns={customerProps}
    deletable={canAdmin}
    visitable
    on:clickrow={clickCustomer}
    on:deleterow={confirmRemoveCustomer}>
    <div slot="rowactions" let:item>
      <button class="dropdown-button-item" on:click={() => confirmResendInvite(item.id)}>
        <SendIcon />
        <div>Resend</div>
      </button>
    </div>
  </Datatable>
</div>

<Modal
  bind:this={removeCustomerModal}
  width="36rem"
  closeable
  on:confirm={removeCustomer}
  buttons={[
    { label: "Cancel", type: "cancel" },
    { label: "Remove", type: "confirm", style: "danger" },
  ]}>
  <div slot="title">Remove Customer</div>
  <div slot="content" class="space-y-4">
    <div>Are you sure you want to remove this customer?</div>
    <div class="px-4">
      <div>Name: <span class="font-bold">{customerToRemove?.name}</span></div>
      <div>Email: <span class="font-bold">{customerToRemove?.primary_contact_email}</span></div>
      <div>Address: <span class="font-bold">{customerToRemove?.address || ""}</span></div>
    </div>
    <div>They will no longer be able to submit requests to your organization.</div>
  </div>
</Modal>

<Modal
  bind:this={newCustomerModal}
  width="36rem"
  closeable
  on:confirm={inviteCustomer}
  buttons={[
    { label: "Cancel", type: "cancel" },
    {
      label: "Invite",
      type: "confirm",
      style: "primary",
      disabled: !isEmail(user_email) || !name,
    },
  ]}>
  <div slot="title">Invite Customer</div>
  <div slot="content" class="space-y-4">
    <TextInput labelWidth="8rem" label="Organization Name" border inputOnKeydown bind:value={name} />
    <TextInput labelWidth="8rem" label="User Email" border inputOnKeydown bind:value={user_email} />
    <TextInput labelWidth="8rem" label="Address" border inputOnKeydown bind:value={address} />
  </div>
</Modal>

<Modal
  bind:this={resendInvitationModal}
  width="36rem"
  closeable
  on:confirm={resendInvite}
  buttons={[
    { label: "Cancel", type: "cancel" },
    {
      label: "Resend Invitation",
      type: "confirm",
      style: "primary",
    },
  ]}>
  <div slot="title">Resend Customer Invitation</div>
  <div slot="content" class="space-y-4">
    {@const customer = customers.find((c) => c.id === invitationToResend)}
    <div>Do you want to resend an invitation to this customer?</div>
    <div>
      {customer?.name} ({customer?.primary_contact_email})
    </div>
  </div>
</Modal>
